/**
 * ProjectSubscriptionProvider
 *
 * 为项目路由提供项目级 WebSocket 订阅上下文。
 * 挂载时订阅 project:{projectId}，卸载时取消订阅。
 * 监听 isConnected 变化，重连后自动重新订阅。
 *
 * 需求：2.1、2.2、2.4、2.5、2.6、2.7
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { useWebSocketContext } from '../services/websocket/WebSocketContext';

// ============================================================================
// Context 类型定义
// ============================================================================

interface ProjectSubscriptionContextType {
  projectId: string;
  isSubscribed: boolean;
}

// ============================================================================
// Context 创建
// ============================================================================

const ProjectSubscriptionContext = createContext<ProjectSubscriptionContextType | null>(null);

// ============================================================================
// Provider Props
// ============================================================================

interface ProjectSubscriptionProviderProps {
  projectId: string;
  children: React.ReactNode;
}

// ============================================================================
// ProjectSubscriptionProvider 组件
// ============================================================================

const ProjectSubscriptionProvider: React.FC<ProjectSubscriptionProviderProps> = ({
  projectId,
  children,
}) => {
  const { client, isConnected } = useWebSocketContext();
  const [isSubscribed, setIsSubscribed] = useState(false);

  useEffect(() => {
    // 未连接或 client 不存在时，不执行订阅
    if (!isConnected || !client) {
      setIsSubscribed(false);
      return;
    }

    let cancelled = false;

    // 订阅 project 房间
    client
      .subscribe({ type: 'project', projectId })
      .then(() => {
        if (!cancelled) {
          setIsSubscribed(true);
        }
      })
      .catch((error: unknown) => {
        // 需求 2.6：订阅失败时记录警告日志，不阻塞渲染
        console.warn(
          `[ProjectSubscriptionProvider] 订阅 project:${projectId} 失败:`,
          error
        );
      });

    // 卸载或依赖变化时取消订阅（需求 2.2）
    return () => {
      cancelled = true;
      setIsSubscribed(false);
      client.unsubscribeProject(projectId).catch((error: unknown) => {
        console.warn(
          `[ProjectSubscriptionProvider] 取消订阅 project:${projectId} 失败:`,
          error
        );
      });
    };
  }, [client, isConnected, projectId]);

  const contextValue: ProjectSubscriptionContextType = {
    projectId,
    isSubscribed,
  };

  return (
    <ProjectSubscriptionContext.Provider value={contextValue}>
      {children}
    </ProjectSubscriptionContext.Provider>
  );
};

export default ProjectSubscriptionProvider;

// ============================================================================
// useProjectSubscription Hook
// ============================================================================

/**
 * 消费 ProjectSubscriptionContext 的 hook。
 * 必须在 ProjectSubscriptionProvider 内部使用，否则抛出错误（需求 2.7）。
 */
export function useProjectSubscription(): ProjectSubscriptionContextType {
  const context = useContext(ProjectSubscriptionContext);
  if (context === null) {
    throw new Error(
      'useProjectSubscription must be used within ProjectSubscriptionProvider'
    );
  }
  return context;
}
